import React from 'react'
import { formatDateDDMonthYYYY } from '../../../../utils/date'
import i18next from 'i18next'

export const FoldersLayout = (props: {
  folderHtml: string
  folderDetails: FolderDetailsProps
  sideMenu: {
    isOpen: boolean
    activeHeaderId?: string
    toggle: () => void
    close: () => void
    setActiveHeader: (activeHeaderId?: string) => void
  }
}) => {
  const { folderHtml, sideMenu, folderDetails } = props

  const htmlParser = new DOMParser()
  const folderDoc = htmlParser.parseFromString(folderHtml, 'text/html')

  const h2Elements = folderDoc.getElementsByTagName('h2')
  const headers =
    h2Elements &&
    Array.from(h2Elements).filter((header) => header.id).map((header) => ({
      headerId: header.id,
      headerLabel: header.innerHTML,
    }))

  React.useEffect(() => {
    if (!headers.length) return

    const hashHeaderId = decodeURIComponent(window.location.hash.replace('#', ''))
    const matchingHashHeaderId = headers.find((header) => header.headerId === hashHeaderId)?.headerId
    const nextActiveHeaderId = matchingHashHeaderId ?? headers[0]?.headerId

    if (nextActiveHeaderId && nextActiveHeaderId !== sideMenu.activeHeaderId) {
      sideMenu.setActiveHeader(nextActiveHeaderId)
    }
  }, [headers, sideMenu])

  React.useEffect(() => {
    const hashHeaderId = decodeURIComponent(window.location.hash.replace('#', ''))
    if (!hashHeaderId) return

    const frameId = window.requestAnimationFrame(() => {
      const targetHeader = document.getElementById(hashHeaderId)
      if (!targetHeader) return

      targetHeader.scrollIntoView()
    })

    return () => window.cancelAnimationFrame(frameId)
  }, [folderHtml])

  return (
    <div className="folder">
      <div className="folder__container">
        <div className="folder__inner text--styled">
          <div dangerouslySetInnerHTML={{ __html: folderHtml }} />
          <FolderDetails {...folderDetails} />
        </div>
        {headers.length > 0 &&
          <FolderSideMenu
            headers={headers}
            menuOpened={sideMenu.isOpen}
            activeHeaderId={sideMenu.activeHeaderId}
            toggleMenu={sideMenu.toggle}
            closeMenu={sideMenu.close}
            setActiveHeader={sideMenu.setActiveHeader}
          />
        }
      </div>
    </div>
  )
}

export type FolderSideMenuProps = {
  headers: { headerId: string; headerLabel: string }[]
  menuOpened: boolean
  activeHeaderId?: string
  toggleMenu: () => void
  closeMenu: () => void
  setActiveHeader: (activeHeaderId?: string) => void
}

export const FolderSideMenu = (props: FolderSideMenuProps) => (
  <section className="fmenu">
    <div className="fmenu__inner">
      <nav className="pageheader__nav nav--pageheader-on-page-nav" aria-label="Secundair">
        <div className="nav__wrap">
          <button
            className="nav__button"
            aria-controls="a11y-ph-nav"
            aria-expanded={props.menuOpened}
            onClick={props.toggleMenu}
          >
            <span className="button__label">{i18next.t('common:goQuicklyTo')}</span>
            <svg className="button__icon" aria-hidden="true" width="12px" height="12px">
              <use xlinkHref="#icon--chevron-down" />
            </svg>
          </button>

          <div className="fmenu__header">{i18next.t('common:directlyTo')}</div>
          <div className="fmenu__items">
            <ul id="a11y-fm-nav" aria-hidden={!props.menuOpened} className="nav__list--on-page-nav">
              {props.headers.map((header) => (
                <li className="nav__item" key={`folder-anchor-${header.headerId}`}>
                  <a
                    href={`#${header.headerId}`}
                    className="nav__link"
                    aria-current={props.activeHeaderId === header.headerId ? 'page' : undefined}
                    onClick={() => {
                      props.setActiveHeader(header.headerId)
                      props.closeMenu()
                    }}
                  >
                    <span>{header.headerLabel}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div className="nav__underlay" aria-hidden="true" onClick={props.toggleMenu} />
        </div>
      </nav>
    </div>
  </section>
)

export type FolderDetailsProps = {
  lastModified: Date
  folderNumber: string
}

export const FolderDetails = (props: FolderDetailsProps) => (
  <section className="fd">
    <div className="fd__item">
      <span>{`${props.folderNumber},`}</span>
    </div>
    <div className="fd__item">
      <time
        dateTime={new Date(props.lastModified).toISOString()}
        title={`${i18next.t('common:updatedOn')} ${formatDateDDMonthYYYY(props.lastModified)}`}
      >
        {`${i18next.t('common:updatedOn')}: ${formatDateDDMonthYYYY(props.lastModified || '')}`}
      </time>
    </div>
  </section>
)
